<template>
  <div
    id="codeEditor"
    class="attached"
    @drop.prevent="dropHandler"
    @dragover.prevent="isDroppable = true"
    @dragleave="isDroppable = false"
    :class="{ droppable: isDroppable }"
  >
    <code-editor-menu
      :suggestions="completions"
      :handleInsert="insert"
      :beautifyCode="beautifyCode"
      @showMinimap="shouldShow => (showMinimap = shouldShow)"
      @editorMenuPinnedChanged="setMenuPinned"
      :class="{ pinned: menuPinned }"
      id="codeEditorMenu"
    />
    <div id="codeArea" @keydown.ctrl.b="beautifyCode">
      <div
        class="ui red right corner big label"
        id="labelDisabled"
        v-if="isFlowEditingDisabled && isCurrentUserAllowedToEditFlow"
      >
        <i class="lock icon"></i>
      </div>
      <monaco-editor
        id="monacoEditor"
        ref="monacoEditor"
        v-model="codeInEditor"
        :readonly="isFlowEditingDisabled || !isCurrentUserAllowedToEditFlow"
        :class="{ outdated: isCurrentFlowVersionOutdated }"
        @changed="codeUpdated"
        @codeSelected="codeSelected"
        :completions="completions"
        :extraLibSource="externalLibSource"
        :showMinimap="showMinimap"
        :allFlows="allFlows"
        :gotoableFields="applicationFields"
        :language="flow.language"
        :flowId="flow.id"
        :squiggles="editorSquiggles"
        :checkDisabledLines="editorCheckDisabledLines"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';

#codeEditor {
  position: relative;
  overflow: hidden;
  z-index: 1;
}
#codeEditorMenu {
  position: absolute;
}
#playpauseMenu {
  position: absolute;
  z-index: 10000;
}

#codeArea {
  position: relative;
  z-index: 1;
  width: calc(100% - 15px);
  height: 100%;
}

#monacoEditor {
  position: absolute;
  z-index: 2;
  height: 100%;
  width: 100%;
}

.outdated {
  opacity: 0.7;
}

#code > #labelDisabled {
  position: absolute;
  z-index: 3;
  right: 0;

  -webkit-transition: all 0.3s, -webkit-transform 0.5s;
  transition: all 0.5s, transform 0.5s;
}

.monaco-editor .find-widget {
  position: relative;
  left: 50%;
}

.monaco-editor .inputarea {
  opacity: 0;
}

.monaco-editor .line-numbers {
  width: 20px !important;
}

.droppable {
  opacity: 0.5;
}

#codeEditorMenu {
  &.pinned {
    & + #codeArea {
      width: calc(100% - 300px);
    }
  }
}
</style>

<script>
// @flow
import {
  CodeCompletions,
  CodeEditor,
  Restrictions,
  BackgroundScheduler,
  Flow,
  Field,
  Group
} from 'services'
import type { AnalysisResult } from 'services/Worker/BackgroundScheduler'
import MonacoEditor from './CodeEditor/MonacoEditor'
import CodeEditorMenu from './CodeEditor/CodeEditorMenu'
import _ from 'lodash'
import { mapFields } from 'vuex-map-fields'
import { mapGetters } from 'vuex'
import type { ModuleIncludeReport } from 'services/CodeEditor'
import { ActionTypes } from 'store'
import Toast from '../../../services/Toast.js'
import FlowImporter from '../../../services/FlowImporter.js'

const defaultCompatibleWithVersion = '1.29'

// We use a WeakMap to allow GC in the _.memoize func
_.memoize.cache = WeakMap

export default {
  name: 'code-editor',
  components: {
    MonacoEditor,
    CodeEditorMenu
  },

  computed: {
    ...mapFields('flowEditor', [
      'codeInEditor',
      'selectedCodeInEditor',
      'variables',
      'fieldsInCode',
      'flowsInCode'
    ]),
    ...mapFields('debugger', ['flowDebugState', 'debugSocket']),
    ...mapGetters(['userPreferences']),
    flow() {
      return this.$store.state.flowEditor.flow || {}
    },
    app() {
      return this.$store.state.flowEditor.app || {}
    },
    allFields() {
      return this.$store.state.field.items
    },
    usedFields() {
      return this.$store.getters['flowEditor/usedFields']()
    },
    applicationFields() {
      return _.filter(this.allFields, { application: this.app.id })
    },
    allFlows() {
      return this.$store.state.flow.items
    },
    isCurrentFlowVersionOutdated() {
      return this.$store.state.flowEditor.isCurrentFlowVersionOutdated
    },
    isCurrentUserAllowedToEditFlow() {
      return this.$store.state.flowEditor.isCurrentUserAllowedToEditFlow
    },
    isFlowEditingDisabled() {
      return this.$store.state.flowEditor.isFlowEditingDisabled
    },
    typeCompletionEnabled() {
      return this.userPreferences.typeCompletion
    },
    snippetCompletionEnabled() {
      return this.userPreferences.snippetCompletion
    },
    mainMenuPinned() {
      return this.$store.state.mainMenuPinned
    }
  },

  props: {
    active: {
      type: Boolean,
      default: () => false
    },
    flowId: {
      type: String,
      default: () => ''
    }
  },

  data() {
    return {
      showSuggestionBox: false,
      showMinimap: false,
      externalLibSource: {},
      menuPinned: false,
      isDroppable: false,
      memoizedCompletions: _.memoize(word =>
        CodeCompletions.getCompletions(
          word,
          this.flow.compatibleVersion || defaultCompatibleWithVersion,
          this.app,
          this.flow,
          this.applicationFields,
          this.allFlows
        )
      ),
      completions: (word, force) => {
        if (force || this.snippetCompletionEnabled) {
          return this.memoizedCompletions(word)
        } else {
          return Promise.resolve([])
        }
      },
      editorSquiggles: { errors: [], warnings: [] },
      editorCheckDisabledLines: [],
      debouncedAnalyser: _.debounce(contentObject => this.analyseCode(contentObject), 200),
      cachedModuleAnalysis: { previousFlowRefs: [], errorReportsByRefName: {} },
      validationDataPromise: null,
      api: {},
      restrictions: []
    }
  },

  methods: {
    setMenuPinned(newValue) {
      // Workaround to the editor not reacting to this width change
      if (newValue !== this.menuPinned) this.$refs.monacoEditor.triggerLayout()
      this.menuPinned = newValue
    },
    insert(text) {
      this.$refs.monacoEditor.insertSuggestion(text)
    },

    codeUpdated(contentObject: Object = {}) {
      this.codeInEditor = contentObject
      // enable logging of transpiled code to console by flipping a flag in dev tools
      if (window.logES5) console.debug('ES5 code\n', contentObject.es5Code)
    },

    signalSave() {
      this.saveRequested = true
      return this.$refs.monacoEditor.signalSave() || this.changePending
    },

    registerTask(description, promise) {
      this.$store.dispatch(ActionTypes.REGISTER_TASK, { task: { description }, promise })
      return promise
    },

    analyseCode(contentObject) {
      if (!contentObject || !contentObject.es5Code) return

      this.registerTask(
        this.$i18n.t('ANALYZE_CODE'),
        BackgroundScheduler.defaultScheduler
          .scheduleCodeAnalysis(
            contentObject.code || contentObject.es5Code,
            !!contentObject.code,
            this.api
          )
          // Wait for data to be loaded before validating
          .then(analysis => this.validationDataPromise.then(() => analysis))
          // Check for bad field refs in included modules (recursively)
          .then((analysis: AnalysisResult) => {
            this.processSemanticComments(analysis.semanticComments)
            if (this.flow.type === 'MODULE') return [analysis, []]
            // Optimization: if the flow reference names are the same as in the last analysis, we pair them up with the results of the last module analysis
            const flowRefNames = analysis.flowRefs.map(fr => fr.matchedRef)
            if (
              this.cachedModuleAnalysis.previousFlowRefs.length &&
              _.isEqual(flowRefNames, this.cachedModuleAnalysis.previousFlowRefs)
            ) {
              const reusedReports: ModuleIncludeReport[] = Object.keys(
                this.cachedModuleAnalysis.errorReportsByRefName
              ).map(refName => ({
                reportLines: this.cachedModuleAnalysis.errorReportsByRefName[refName],
                ref: _.find(analysis.flowRefs, fr => fr.matchedRef === refName)
              }))
              return [analysis, reusedReports]
              // End of optimization
            }
            return CodeEditor.getModuleIncludesWithBadFieldRefs(
              analysis.flowRefs,
              this.allFlows,
              this.applicationFields
            ).then((badRefReports: ModuleIncludeReport[]) => {
              this.cachedModuleAnalysis.previousFlowRefs = flowRefNames
              this.cachedModuleAnalysis.errorReportsByRefName = {} // Reset cache
              badRefReports.forEach(
                brr =>
                  (this.cachedModuleAnalysis.errorReportsByRefName[brr.ref.matchedRef] =
                    brr.reportLines)
              )
              return [analysis, badRefReports]
            })
          })
          .then(([analysis, badRefReports]) => {
            this.updateAccessedFlowVariables(contentObject.es5Code, analysis)
            this.createAnalysisSquiggles(analysis, badRefReports)
          })
          .catch(error => {
            // Show the espree error in monaco if it has index info
            if (error.index != null) {
              this.editorSquiggles = {
                errors: [
                  {
                    index: Math.max(0, error.index - 1),
                    length: 3,
                    message: `Parser error: ${error.message}`
                  }
                ],
                warnings: []
              }
            }
            console.warn(error)
            return error
          })
          .then(error => {
            this.changePending = false
            // Emit the event that code was parsed along with any resulting parse error
            this.$emit(
              'codeParsed',
              contentObject.code || contentObject.es5Code,
              contentObject.saveRequested || this.saveRequested,
              error ? error.message : null
            )
          })
      )
    },

    processSemanticComments(semanticComments) {
      this.editorCheckDisabledLines = semanticComments
        .filter(c => c.matchedRef === 'disable-check')
        .map(c => c.lineStart)
    },

    createAnalysisSquiggles(analysis: AnalysisResult, badRefReports: ModuleIncludeReport[]) {
      let warnings = []
      let errors = []
      if (this.flow.type !== 'MODULE') {
        // Verify field references
        const badFieldRefs = analysis.fieldRefs.filter(fr =>
          _.every(this.applicationFields, af => af.name !== fr.matchedRef)
        )
        const badIncludeSquiggles = badRefReports
          .filter(brr => brr && brr.reportLines && brr.reportLines.length > 0)
          .map(brr => ({
            ...brr.ref,
            message: this.$i18n.t('INCLUDE_WITH_BAD_REFERENCES_ERROR', {
              name: brr.ref.matchedRef,
              lines: brr.reportLines.join('\n')
            })
          }))
        errors = badFieldRefs
          .map(bfr => ({
            ...bfr,
            message: this.$i18n.t('UNDEFINED_REFERENCE_ERROR', {
              thing: this.$i18n.tc('FIELD', 1).toLowerCase(),
              thingName: bfr.matchedRef
            })
          }))
          .concat(badIncludeSquiggles)
        const positionalRefs = CodeEditor.getCodeFieldsUsingPositionalAnchors(
          this.applicationFields,
          analysis.fieldRefs
        )
        warnings = positionalRefs.map(pr => ({
          ...pr,
          message: this.$i18n.t('POSITIONAL_FIELDS_USED_EX', {
            positional: CodeEditor.positionalAnchors.join(', ')
          })
        }))
      }
      // Verify flow references
      const badFlowRefs = CodeEditor.getNoneExistingFlowsFromMatches(
        analysis.flowRefs,
        this.allFlows
      )

      // add warning/errors when flow restrictions are violated
      let restrictionSquiggles = _.filter(
        _.flatMap(this.restrictions, rstr =>
          _.flatMap(rstr.restrictions, r => {
            // If we've seen the restricted api in the flow then we need a warning
            var usage = _.get(analysis.apiUsage, [r.module, r.method], false)
            if (usage) {
              return _.map(usage, u => ({
                ...u,
                restrictionId: rstr.id,
                type: _.find(r.actions, a => a.kind === 'DENY') ? 'error' : 'warning',
                message: this.$i18n.t('RESTRICTION_WARNING', {
                  forVersion: rstr.forVersion,
                  forGroup: rstr.forGroup,
                  module: r.module,
                  method: r.method,
                  actions: _.join(
                    _.map(_.map(r.actions, 'kind'), k =>
                      this.$i18n.t('RESTRICTION_ACTION_KIND_' + k)
                    ),
                    ', '
                  )
                })
              }))
            }
          })
        )
      )

      errors = [
        ...errors,
        ...badFlowRefs.map(bfr => ({
          ...bfr,
          message: this.$i18n.t('UNDEFINED_REFERENCE_ERROR', {
            thing: this.$i18n.tc('FLOW', 1).toLowerCase(),
            thingName: bfr.matchedRef
          })
        }))
      ]

      this.editorSquiggles = {
        errors: [...errors, ..._.filter(restrictionSquiggles, s => s.type === 'error')],
        warnings: [...warnings, ..._.filter(restrictionSquiggles, s => s.type === 'warning')]
      }
    },

    codeSelected(contentObject: Object = {}) {
      this.selectedCodeInEditor = contentObject
    },
    beautifyCode() {
      this.$refs.monacoEditor.beautifyCode()
    },

    focus() {
      this.$refs.monacoEditor.focus()
      this.$refs.monacoEditor.triggerLayout()
    },

    updateAccessedFlowVariables(code, analysis) {
      this.variables = analysis.vars.map(v => ({ name: v, id: v }))
      // Update used fields in flow code.
      this.fieldsInCode = CodeEditor.getIdsForMatchedElementsNames(
        _.uniq(analysis.fieldRefs.map(fm => fm.matchedRef)),
        this.applicationFields
      )
      this.flowsInCode = CodeEditor.getIdsForMatchedElementsNames(
        _.uniq(analysis.flowRefs.map(fm => fm.matchedRef)),
        this.allFlows
      )
    },

    updateTypeScriptLib() {
      CodeCompletions.getTypeScriptLib(
        this.flow.compatibleVersion || defaultCompatibleWithVersion,
        this.applicationFields,
        this.flow
      ).then(lib => {
        if (!this.externalLibSource || this.externalLibSource.source !== lib.source)
          this.externalLibSource = lib
      })
    },

    dropHandler(e) {
      this.isDroppable = false
      let file = e.dataTransfer.items[0].getAsFile()
      try {
        FlowImporter.fileParser(file, this.insert)
      } catch (e) {
        Toast.error(this.$t('INVALID_FILE'))
      }
    }
  },

  mounted() {
    if (this.typeCompletionEnabled) this.updateTypeScriptLib()
    else this.externalLibSource = {}
    this.debouncedAnalyser(this.codeInEditor)
    // We need to keep track of when data used for reference checking is ready, so we don't flag valid references
    this.validationDataPromise = Promise.all([Flow.readAllFlows(), Field.readFields()])
  },

  watch: {
    applicationFields: function() {
      if (this.typeCompletionEnabled) this.updateTypeScriptLib()
    },
    flow: function() {
      if (this.typeCompletionEnabled) this.updateTypeScriptLib()

      // Load relevant restrictions
      Group.readGroupsForFlow(this.flow.id).then(groups => {
        const groupIds = _.map(groups, 'id')
        Restrictions.readAll().then(all => {
          // Applicable restrictions are those with a matching version and group
          this.restrictions = _.filter(
            all,
            restriction =>
              restriction.forVersion === this.flow.compatibleVersion &&
              groupIds.indexOf(restriction.forGroup) >= 0
          )
        })
      })

      // Load api for content declaration and restriction check
      CodeCompletions.resolveSuggestions(this.flow.compatibleVersion).then(s => {
        this.api = _.reduce(
          s?.api?.lib ?? [],
          (memo, module) => {
            memo[module.type] = _.keyBy(module.properties, 'name')
            return memo
          },
          {}
        )
      })
    },
    active(newVal) {
      if (newVal)
        setTimeout(() => {
          this.focus()
          this.$refs.monacoEditor.updateContents(this.codeInEditor)
        }, 200)
    },
    mainMenuPinned() {
      setTimeout(() => this.focus(), 500) // Delay so animation can complete first
    },
    flowId(newVal) {
      if (newVal) {
        this.cachedModuleAnalysis.previousFlowRefs = []
        this.cachedModuleAnalysis.errorReportsByRefName = {}
        setTimeout(() => this.focus(), 200)
      }
    },
    codeInEditor(newVal) {
      this.saveRequested = false // We only care about saves signaled between this point and the emitting of 'codeParsed'
      this.changePending = true
      this.debouncedAnalyser(newVal)
    }
  }
}
</script>
